/**
 * Network Hierarchy Visibility Store - Zustand Store
 *
 * @version 1.0
 * @module core/spatial/stores
 *
 * @description
 * Store simple para controlar la visibilidad de la capa de Jerarquía Morfológica.
 * Sigue el patrón Zustand establecido en la app.
 *
 * @example
 * ```tsx
 * import { useNetworkHierarchyVisibilityStore } from '@/core/spatial/stores';
 *
 * function MyComponent() {
 *   const { visible, setVisible } = useNetworkHierarchyVisibilityStore();
 *
 *   return (
 *     <button onClick={() => setVisible(!visible)}>
 *       Toggle Hierarchy
 *     </button>
 *   );
 * }
 * ```
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

// ============================================================================
// TYPES
// ============================================================================

export interface NetworkHierarchyVisibilityState {
  /** Si la capa de jerarquía morfológica está visible */
  visible: boolean;
}

export interface NetworkHierarchyVisibilityActions {
  /** Establece la visibilidad de la capa */
  setVisible: (visible: boolean) => void;
  /** Reset al estado inicial */
  reset: () => void;
}

export type NetworkHierarchyVisibilityStore = NetworkHierarchyVisibilityState &
  NetworkHierarchyVisibilityActions;

// ============================================================================
// INITIAL STATE
// ============================================================================

const initialState: NetworkHierarchyVisibilityState = {
  visible: false,
};

// ============================================================================
// STORE
// ============================================================================

/**
 * Store de visibilidad para Jerarquía Morfológica
 *
 * @remarks
 * Store simple que solo gestiona el toggle de visibilidad.
 * No maneja cálculos ni datos - eso es responsabilidad de los hooks y servicios.
 */
export const useNetworkHierarchyVisibilityStore = create<NetworkHierarchyVisibilityStore>()(
  devtools(
    (set, get) => ({
      // Estado inicial
      ...initialState,

      // Acciones
      setVisible: (visible: boolean): void => {
        const current = get().visible;

        // No hacer nada si ya está en ese estado
        if (current === visible) {
          return;
        }

        set({ visible });

        console.debug(`[NetworkHierarchyVisibility] Visibility changed: ${current} → ${visible}`);
      },

      reset: (): void => {
        set(initialState);
        console.debug('[NetworkHierarchyVisibility] Reset to initial state');
      },
    }),
    { name: 'network-hierarchy-visibility-store' }
  )
);

// ============================================================================
// SELECTORS
// ============================================================================

/**
 * Selector para obtener la visibilidad actual
 */
export const selectHierarchyVisible = (state: NetworkHierarchyVisibilityStore): boolean =>
  state.visible;
